import { useState, useEffect } from "react";
import { List, Package, Search, Building2, Filter, Download, ChevronDown, ChevronRight } from "lucide-react";
import axios from "axios";
import { API } from "../contexts/AuthContext";
import { formatPYGCompact } from "../utils/format";
import { Skeleton } from "../components/ui/skeleton";
import { toast } from "sonner";

const STATUS_BADGE = {
  borrador: { label: "Borrador", cls: "bg-gray-100 text-gray-600" },
  enviado: { label: "Enviado", cls: "bg-blue-100 text-blue-700" },
  aprobado: { label: "Aprobado", cls: "bg-emerald-100 text-emerald-700" },
  rechazado: { label: "Rechazado", cls: "bg-red-100 text-red-700" },
};

export default function IngenieriaLista() {
  const [budgets, setBudgets] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedProject, setSelectedProject] = useState("todos");
  const [expandedBudgets, setExpandedBudgets] = useState(new Set());

  useEffect(() => {
    const fetch = async () => {
      try {
        const [bRes, pRes] = await Promise.all([
          axios.get(`${API}/budgets`, { withCredentials: true }),
          axios.get(`${API}/projects`, { withCredentials: true }),
        ]);
        setBudgets(bRes.data || []);
        setProjects(pRes.data || []);
      } catch {
        toast.error("Error al cargar listas");
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  const projectMap = Object.fromEntries(projects.map(p => [p._id, p.name]));

  const filteredBudgets = budgets.filter(b => {
    const matchProject = selectedProject === "todos" || b.project_id === selectedProject;
    const matchSearch = !search || b.name?.toLowerCase().includes(search.toLowerCase()) || b.client?.toLowerCase().includes(search.toLowerCase());
    return matchProject && matchSearch;
  });

  // All items aggregated
  const allItems = filteredBudgets.flatMap(b => (b.items || []).map(item => ({
    ...item,
    budget_name: b.name,
    budget_status: b.status,
    project_name: projectMap[b.project_id] || "—",
  })));

  // Group by category
  const byCategory = allItems.reduce((acc, item) => {
    const cat = item.category || "Sin Categoría";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(item);
    return acc;
  }, {});

  const totalValue = allItems.reduce((a, i) => a + (Number(i.quantity) * Number(i.unit_price)), 0);
  const totalItems = allItems.length;
  const totalCategories = Object.keys(byCategory).length;

  const toggleBudget = (id) => {
    setExpandedBudgets(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <span className="text-xs font-bold text-violet-600 bg-violet-50 px-2 py-0.5 rounded-full uppercase tracking-wide">Ingeniería</span>
        </div>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">Lista</h1>
        <p className="text-sm text-gray-500 mt-0.5">Listas de materiales y ítems de todos los presupuestos activos</p>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: "Total Ítems", value: totalItems, icon: List },
          { label: "Categorías", value: totalCategories, icon: Filter },
          { label: "Valor Total Est.", value: formatPYGCompact(totalValue), icon: Package },
        ].map(({ label, value, icon: Icon }) => (
          <div key={label} className="bg-white rounded-xl border border-gray-200/60 p-5 shadow-sm hover:shadow-md transition-all">
            <div className="flex items-center justify-between mb-1">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{label}</p>
              <Icon size={15} className="text-violet-500" />
            </div>
            <p className="text-xl font-bold text-gray-900">{value}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-xl bg-white focus:ring-2 focus:ring-violet-200 transition-all" placeholder="Buscar presupuesto..." value={search} onChange={e => setSearch(e.target.value)} data-testid="lista-search" />
        </div>
        <select className="border border-gray-200 rounded-xl px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-violet-200 transition-all" value={selectedProject} onChange={e => setSelectedProject(e.target.value)}>
          <option value="todos">Todos los proyectos</option>
          {projects.map(p => <option key={p._id} value={p._id}>{p.name}</option>)}
        </select>
      </div>

      {/* Budget list with expandable items */}
      <div className="space-y-3">
        {loading ? (
          Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-16 w-full rounded-xl" />)
        ) : filteredBudgets.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-200/60 p-12 text-center">
            <List size={40} className="mx-auto mb-3 text-gray-300" />
            <p className="text-gray-500 font-medium">No hay presupuestos disponibles</p>
            <p className="text-xs text-gray-400 mt-1">Crea presupuestos en Ingeniería → Presupuestos</p>
          </div>
        ) : (
          filteredBudgets.map(budget => {
            const isExpanded = expandedBudgets.has(budget._id);
            const badge = STATUS_BADGE[budget.status] || { label: budget.status, cls: "bg-gray-100 text-gray-600" };
            const itemCount = budget.items?.length || 0;
            const totalBudget = budget.items?.reduce((a, i) => a + (Number(i.quantity) * Number(i.unit_price)), 0) || 0;

            return (
              <div key={budget._id} className="bg-white rounded-2xl border border-gray-200/60 shadow-sm overflow-hidden">
                <button
                  onClick={() => toggleBudget(budget._id)}
                  className="w-full flex items-center justify-between px-5 py-4 hover:bg-gray-50/50 transition-colors text-left"
                  data-testid={`lista-budget-${budget._id}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    {isExpanded ? <ChevronDown size={16} className="text-gray-400 flex-shrink-0" /> : <ChevronRight size={16} className="text-gray-400 flex-shrink-0" />}
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-gray-900 truncate">{budget.name}</p>
                      <p className="text-xs text-gray-400">{projectMap[budget.project_id] || "Sin proyecto"} · {budget.client}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 flex-shrink-0">
                    <div className="text-right hidden sm:block">
                      <p className="text-sm font-bold text-gray-900">{formatPYGCompact(totalBudget)}</p>
                      <p className="text-xs text-gray-400">{itemCount} ítems</p>
                    </div>
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${badge.cls}`}>{badge.label}</span>
                  </div>
                </button>

                {isExpanded && budget.items && budget.items.length > 0 && (
                  <div className="border-t border-gray-100 overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-gray-50/80">
                          <th className="text-left px-5 py-2.5 text-xs font-bold text-gray-400 uppercase tracking-wide">Descripción</th>
                          <th className="text-left px-4 py-2.5 text-xs font-bold text-gray-400 uppercase tracking-wide">Categoría</th>
                          <th className="text-right px-4 py-2.5 text-xs font-bold text-gray-400 uppercase tracking-wide">Cant.</th>
                          <th className="text-left px-4 py-2.5 text-xs font-bold text-gray-400 uppercase tracking-wide">Unidad</th>
                          <th className="text-right px-5 py-2.5 text-xs font-bold text-gray-400 uppercase tracking-wide">P. Unitario</th>
                          <th className="text-right px-5 py-2.5 text-xs font-bold text-gray-400 uppercase tracking-wide">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {budget.items.map((item, j) => (
                          <tr key={j} className="border-t border-gray-50 hover:bg-gray-50/30 transition-colors">
                            <td className="px-5 py-2.5 text-sm text-gray-800">{item.description}</td>
                            <td className="px-4 py-2.5 text-xs text-gray-500">{item.category || "—"}</td>
                            <td className="px-4 py-2.5 text-right text-sm text-gray-700">{item.quantity}</td>
                            <td className="px-4 py-2.5 text-sm text-gray-500">{item.unit}</td>
                            <td className="px-5 py-2.5 text-right text-sm text-gray-700">{formatPYGCompact(item.unit_price)}</td>
                            <td className="px-5 py-2.5 text-right text-sm font-semibold text-gray-900">{formatPYGCompact(Number(item.quantity) * Number(item.unit_price))}</td>
                          </tr>
                        ))}
                        <tr className="border-t-2 border-gray-200 bg-gray-50/50">
                          <td colSpan={5} className="px-5 py-2.5 text-sm font-bold text-gray-700 text-right">Subtotal:</td>
                          <td className="px-5 py-2.5 text-right text-sm font-bold text-gray-900">{formatPYGCompact(totalBudget)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Category breakdown */}
      {!loading && allItems.length > 0 && (
        <div className="bg-white rounded-2xl border border-gray-200/60 shadow-sm p-5">
          <h3 className="text-sm font-bold text-gray-900 mb-4">Distribución por Categoría</h3>
          <div className="space-y-3">
            {Object.entries(byCategory).sort((a, b) => b[1].reduce((s, i) => s + Number(i.quantity) * Number(i.unit_price), 0) - a[1].reduce((s, i) => s + Number(i.quantity) * Number(i.unit_price), 0)).map(([cat, items]) => {
              const catTotal = items.reduce((a, i) => a + Number(i.quantity) * Number(i.unit_price), 0);
              const pct = totalValue > 0 ? Math.round((catTotal / totalValue) * 100) : 0;
              return (
                <div key={cat}>
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-xs font-semibold text-gray-700">{cat}</p>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-400">{items.length} ítems</span>
                      <span className="text-xs font-bold text-gray-900">{formatPYGCompact(catTotal)}</span>
                      <span className="text-xs text-gray-400">({pct}%)</span>
                    </div>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-1.5">
                    <div className="bg-violet-500 h-1.5 rounded-full transition-all duration-700" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
